/**
 * Profits & Expenses container (C25, B4 D06 part 2 — D-098 – D-102; D-254 – D-256): binds `Financials` (packages/ui,
 * old App.tsx:31769-33700) to the read model `GET /profits` and opens the four dialogs.
 *
 *   figures  : one period for both tabs (the old selector set both); everything — header cards per currency, rows,
 *              summaries, daily reports, liquidation list, expenses — comes from the server's one profit engine
 *              (D-098); the tabs' search and log-date filters go to the server too. Office dates throughout (D-098).
 *   actions  : capitalization (`profit_capitalize`, D-099), profit withdrawal (`profit_withdraw`, D-100), USD profit
 *              conversion (`profit_conversion`, D-101: the IQD figure is the server preview's), expense (`expense`,
 *              D-102) — each preview → PIN → commit through the gateway (D-232); the trash buttons cancel the whole
 *              operation through the journal (reason → own PIN → reversal, D-091, D-102).
 *   prints   : the old two A4 reports — "تقرير حركات تصفية الأرباح" (p03: the liquidation list, App.tsx:23370-23398) and
 *              "كشف المصاريف" (App.tsx:23400-23430) — printed through the browser until the PDF worker of B5.
 * Labels are formatted here; no money is computed (display sums of decimal strings only).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import {
  AddExpenseModal, CapitalizeProfitModal, ConvertProfitModal, Financials, JournalCancelDialog, WithdrawProfitModal, parseCommas, toEnglishDigits,
  type AddExpenseValues, type ConvertProfitValues, type FinancialsAmount, type FinancialsPeriod, type FinancialsProfitRow, type FinancialsTab, type FinancialsViewMode,
  type ProfitSettlementValues,
} from "@faylasuf/ui";
import type { Money, PreviewResult, ProfitsQuery, ProfitsView } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { errorMessage, isSessionLost } from "../app/errors.js";
import { useGateway } from "../app/operations.js";
import { usePinGate } from "../app/pin-gate.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { decAdd, fmt, isZero } from "./customers/format.js";
import { itemOf } from "./JournalPage.js";
import { usePrintOffice } from "../app/printOffice.js";

/** The old movement type shown in the profit row's badge (App.tsx:32892-32895 printed `m.type`). */
const OLD_TYPE: Readonly<Record<string, string>> = {
  usd_buy: "buy",
  usd_sell: "sell",
  currency_buy: "currency_buy",
  currency_sell: "currency_sell",
  currency_sell_for_usd: "currency_sell",
  quick_deposit: "deposit",
  quick_withdraw: "withdraw",
  customer_deposit: "deposit",
  customer_withdraw: "withdraw",
  manual_work: "manual_work",
  transfer_out: "cash_transfer",
  transfer_in: "cash_transfer",
  transfer_out_settled: "cash_transfer",
  transfer_in_settled: "cash_transfer",
  transfer_currency_passthrough: "cash_transfer",
  profit_conversion: "convert_usd_profit",
  account_conversion: "conversion",
  currency_writeoff: "currency_sell",
};

/** Operation types that can carry profit lines (searched by their displayed label) and the profit categories. */
const SEARCHABLE_TYPES: readonly string[] = [...Object.keys(OLD_TYPE), "partner_charge", "usd_direct_deposit", "currency_direct_deposit"];
const PROFIT_CATEGORIES: readonly string[] = ["usd_trading", "currency_trading", "cash_commission", "digital_commission", "customer_commission", "transfer_commission", "manual_work", "profit_conversion"];

const addDays = (ymd: string, days: number): string => {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};
const amountOf = (v: string): string => toEnglishDigits(parseCommas(v)).trim();
const usdLabel = (v: string | undefined): string | null => (v && !isZero(v) ? `$${fmt(v)}` : null);
const amountView = (m: Money): FinancialsAmount => ({ iqd: fmt(m["IQD"] ?? "0"), usd: usdLabel(m["USD"]) });

/** `initialTab` [C26]: the dashboard expenses card opens the expenses tab (App.tsx:26968-26971). */
export function FinancialsPage({ initialTab = "profits" }: { initialTab?: FinancialsTab } = {}) {
  const { t, locale, source } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const gateway = useGateway();
  const tz = session.office.timeZone;
  const lang = locale;

  const [period, setPeriod] = useState<FinancialsPeriod>("day");
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tab, setTab] = useState<FinancialsTab>(initialTab);
  const [profitMode, setProfitMode] = useState<FinancialsViewMode>("logs");
  const [expenseMode, setExpenseMode] = useState<FinancialsViewMode>("logs");
  const [profitSearch, setProfitSearch] = useState("");
  const [profitDate, setProfitDate] = useState("");
  const [expenseSearch, setExpenseSearch] = useState("");
  const [expenseDate, setExpenseDate] = useState("");

  // the searches go to the server after a pause in typing (each request reads the whole period)
  const [profitQ, setProfitQ] = useState("");
  const [expenseQ, setExpenseQ] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setProfitQ(profitSearch.trim()), 300);
    return () => clearTimeout(h);
  }, [profitSearch]);
  useEffect(() => {
    const h = setTimeout(() => setExpenseQ(expenseSearch.trim()), 300);
    return () => clearTimeout(h);
  }, [expenseSearch]);
  // the old search read the displayed item text: the types / categories whose label (in this language) contains the text
  const profitMatch = profitQ
    ? [
        ...SEARCHABLE_TYPES.filter((ty) => itemOf(t, ty, {}).toLowerCase().includes(profitQ.toLowerCase()) || t(`jr.type.${ty}`).toLowerCase().includes(profitQ.toLowerCase())),
        ...PROFIT_CATEGORIES.filter((c) => t(`pf.cat.${c}`).toLowerCase().includes(profitQ.toLowerCase())),
      ].join(",")
    : "";
  const query: ProfitsQuery = {
    period,
    ...(period === "day" && day ? { day } : {}),
    ...(period === "month" && month ? { month } : {}),
    ...(period === "year" && year ? { year } : {}),
    ...(period === "custom" && from ? { from } : {}),
    ...(period === "custom" && to ? { to } : {}),
    ...(profitQ ? { profitSearch: profitQ } : {}),
    ...(profitMatch ? { profitMatch } : {}),
    ...(profitDate ? { profitDate } : {}),
    ...(expenseQ ? { expenseSearch: expenseQ } : {}),
    ...(expenseDate ? { expenseDate } : {}),
  };
  const q = useQuery({ queryKey: [...keys.profits, query], queryFn: () => source.profits(query), staleTime: 5_000, placeholderData: (prev) => prev });
  if (q.error && isSessionLost(q.error)) sessionLost();
  const data: ProfitsView | undefined = q.data;
  const today = data?.today ?? "";
  // the office's today fills the selectors once known (old: UTC today, App.tsx:4036-4053; D-098 office dates)
  useEffect(() => {
    if (!today) return;
    setDay((d) => d || today);
    setMonth((m) => m || today.slice(0, 7));
    setYear((y) => y || today.slice(0, 4));
    setFrom((f) => f || addDays(today, -30));
    setTo((x) => x || today);
  }, [today]);

  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };
  const invalidate = () => {
    for (const k of [keys.profits, keys.journal, keys.accounts, keys.dashboard, keys.dollar, keys.capitalOperations]) void qc.invalidateQueries({ queryKey: k });
  };

  // ---------------------------------------------------------------- labels (office dates, the old cell formats)
  const dateCell = (officeDay: string): string => {
    const [y, m, d] = officeDay.split("-").map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).replace(/\//g, "-");
  };
  const timeCell = (officeDay: string, hhmm: string): string => {
    const [y, m, d] = officeDay.split("-").map(Number) as [number, number, number];
    const [hh, mm] = hhmm.split(":").map(Number) as [number, number];
    return new Date(Date.UTC(y, m - 1, d, hh, mm)).toLocaleTimeString(lang === "ar" ? "ar-EG" : "en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
  };
  const weekday = (officeDay: string): string => new Date(`${officeDay}T12:00:00Z`).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { weekday: "long", timeZone: "UTC" });
  const moneyText = (m: Money): string =>
    Object.entries(m)
      .filter(([, v]) => !isZero(v))
      .map(([c, v]) => (c === "IQD" ? `${fmt(v)} د.ع` : c === "USD" ? `${fmt(v)} $` : `${fmt(v)} ${c}`))
      .join(" + ") || "0 د.ع";

  const view = useMemo(() => {
    if (!data) return null;
    const p = data.profits;
    const e = data.expenses;
    const summary = (list: ReadonlyArray<{ name: string; currency: string; amount: string }>) =>
      list.map((x) => ({ name: x.currency === "USD" && !x.name.includes("($)") ? `${x.name} ($)` : x.name, amount: fmt(x.amount), usd: x.currency === "USD" }));
    const salesTotal: Record<string, string> = {};
    for (const s of p.currencySales ?? []) for (const [c, v] of Object.entries(s.total)) salesTotal[c] = decAdd(salesTotal[c] ?? "0", v);
    const profitRows: FinancialsProfitRow[] = p.rows.map((r) => ({
      key: `${r.id}:${r.currency}`,
      id: r.id,
      dateLabel: dateCell(r.officeDate),
      timeLabel: timeCell(r.officeDate, r.time),
      item: itemOf(t, r.type, r.params),
      typeBadge: OLD_TYPE[r.type] ?? r.type,
      isBuy: r.type === "usd_buy" || r.type === "currency_buy",
      operator: r.operator,
      note: r.note,
      wallet: r.boxName,
      amountLabel: fmt(r.amount),
      negative: r.amount.startsWith("-"),
      usd: r.currency === "USD",
      cancellable: r.cancellable,
    }));
    return {
      header: { revenue: amountView(data.header.revenue), profit: amountView(data.header.profit), expense: amountView(data.header.expenses), net: amountView(data.header.net), netNegative: (data.header.net["IQD"] ?? data.header.net["USD"] ?? "0").startsWith("-") },
      profitTotal: amountView(p.total),
      byBox: summary(p.byBox.map((x) => ({ ...x, name: x.name ?? t("pf.main_vault") }))),
      byCategory: summary(p.byCategory.map((x) => ({ name: x.category ? t(`pf.cat.${x.category}`) : t("pf.other_cat"), currency: x.currency, amount: x.amount }))),
      byOperator: summary(p.byOperator.map((x) => ({ ...x, name: x.name ?? t("pf.unknown") }))),
      currencySales: (p.currencySales ?? []).map((s) => ({ code: s.currency, count: s.count, totalLabel: moneyText(s.total) })),
      currencySalesTotal: moneyText(salesTotal),
      profitDaily: p.daily.map((d) => ({ date: d.date, weekday: weekday(d.date), count: d.count, totalLabel: fmt(d.total["IQD"] ?? "0"), usdLabel: usdLabel(d.total["USD"]), pct: d.pct })),
      profitRows,
      liquidation: p.liquidation.map((l) => ({ id: l.id, dateLabel: dateCell(l.officeDate), withdraw: l.type === "profit_withdraw", amountLabel: l.currency === "USD" ? `$${fmt(l.amount)}` : `${fmt(l.amount)} د.ع`, note: l.note, cancellable: l.cancellable })),
      expenseRows: e.rows.map((r) => ({
        id: r.id,
        dateLabel: dateCell(r.officeDate),
        timeLabel: timeCell(r.officeDate, r.time),
        category: r.cardKey,
        operator: r.operator,
        details: r.category === "salary" && r.employee ? `${t("pf.salary_for", { name: r.employee })}${r.note ? ` | ${r.note}` : ""}` : r.note,
        amountLabel: fmt(r.amount),
        cancellable: r.cancellable,
      })),
      expenseCards: Object.fromEntries(Object.entries(e.byCard).map(([k, c]) => [k, { total: fmt(c.total), count: c.count, pct: c.pct }])) as Record<"salary" | "tip" | "outlet" | "withdrawal_comm" | "other", { total: string; count: number; pct: string }>,
      expenseOther: { total: fmt(e.otherCard.total), count: e.otherCard.count, pct: e.otherCard.pct },
      salaries: e.salaries.map((s) => ({ name: s.name || t("pf.unknown"), amount: fmt(s.amount) })),
      expenseOperators: e.byOperator.map((o) => ({ name: o.name || t("pf.unknown"), count: o.count, total: fmt(o.total) })),
      expenseDaily: e.daily.map((d) => ({ date: d.date, weekday: weekday(d.date), count: d.count, totalLabel: fmt(d.total), usdLabel: null, pct: d.pct })),
    };
  }, [data, t, lang]);

  // ---------------------------------------------------------------- view modes ("عرض الكل" = all data + detailed log)
  const onMode = (set: (m: FinancialsViewMode) => void) => (m: FinancialsViewMode | "all") => {
    if (m === "all") {
      setPeriod("all");
      set("logs");
    } else set(m);
  };

  // ---------------------------------------------------------------- dialogs
  const pendingLabel = { IQD: fmt(data?.pending["IQD"] ?? "0"), USD: data && !data.usdHidden ? fmt(data.pending["USD"] ?? "0") : null };
  const [busy, setBusy] = useState(false);
  const emptySettle: ProfitSettlementValues = { currency: "IQD", amount: "", note: "" };
  const [capOpen, setCapOpen] = useState(false);
  const [capValues, setCapValues] = useState<ProfitSettlementValues>(emptySettle);
  const [wdOpen, setWdOpen] = useState(false);
  const [wdValues, setWdValues] = useState<ProfitSettlementValues>(emptySettle);
  const [convOpen, setConvOpen] = useState(false);
  const [convValues, setConvValues] = useState<ConvertProfitValues>({ amount: "", rate: "", note: "" });
  const [convPreview, setConvPreview] = useState<{ key: string; pv: PreviewResult } | null>(null);
  const [expOpen, setExpOpen] = useState(false);
  const [expValues, setExpValues] = useState<AddExpenseValues>({ category: "salary", amount: "", note: "", employeeId: "" });

  const post = async (type: string, input: Record<string, unknown>, pinMessage: string, successKey: string, close: () => void) => {
    setBusy(true);
    try {
      const res = await gateway.run(type, input, { pinMessage, successKey, allowSupervisor: true });
      if (res) {
        close();
        invalidate();
      }
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };
  const submitSettlement = (kind: "capitalize" | "withdraw") => {
    const v = kind === "capitalize" ? capValues : wdValues;
    const amount = amountOf(v.amount);
    if (!amount) return toast(t("pf.err_amount"), "error");
    const input: Record<string, unknown> = { currency: v.currency, amount, ...(v.note.trim() ? { note: v.note.trim() } : {}) };
    if (kind === "withdraw") input["accountId"] = data?.vaults[v.currency] ?? "";
    const label = v.currency === "USD" ? `$${fmt(amount)}` : `${fmt(amount)} د.ع`;
    void post(
      kind === "capitalize" ? "profit_capitalize" : "profit_withdraw",
      input,
      t(kind === "capitalize" ? "pf.pin_capitalize" : "pf.pin_withdraw", { amount: label }),
      kind === "capitalize" ? "pf.toast_capitalized" : "pf.toast_withdrawn",
      () => {
        if (kind === "capitalize") {
          setCapOpen(false);
          setCapValues(emptySettle);
        } else {
          setWdOpen(false);
          setWdValues(emptySettle);
        }
      },
    );
  };

  // conversion: the IQD figure is the server preview's (D-101, D-012), refreshed as the operator types
  const convInput = (): Record<string, unknown> | null => {
    const usd = amountOf(convValues.amount);
    const quote = amountOf(convValues.rate);
    if (!usd || !quote || !data?.vaults.USD || !data.vaults.IQD) return null;
    return { usd, quote, usdVaultAccountId: data.vaults.USD, iqdVaultAccountId: data.vaults.IQD, ...(convValues.note.trim() ? { note: convValues.note.trim() } : {}) };
  };
  const convSeq = useRef(0);
  useEffect(() => {
    if (!convOpen) return;
    const input = convInput();
    if (!input) {
      setConvPreview(null);
      return;
    }
    const key = JSON.stringify(input);
    const seq = ++convSeq.current;
    const h = setTimeout(() => {
      gateway
        .preview("profit_conversion", input)
        .then((pv) => {
          if (seq === convSeq.current) setConvPreview({ key, pv });
        })
        .catch(() => {
          if (seq === convSeq.current) setConvPreview(null);
        });
    }, 300);
    return () => clearTimeout(h);
  }, [convOpen, convValues.amount, convValues.rate, convValues.note, data?.vaults.USD, data?.vaults.IQD]);
  const convKey = (() => {
    const i = convInput();
    return i ? JSON.stringify(i) : null;
  })();
  const submitConversion = async () => {
    const input = convInput();
    if (!input) return toast(t("pf.err_amount"), "error");
    setBusy(true);
    try {
      const key = JSON.stringify(input);
      let pv: PreviewResult;
      try {
        pv = convPreview && convPreview.key === key ? convPreview.pv : await gateway.preview("profit_conversion", input);
      } catch (e) {
        fail(e); // the server's refusal (e.g. more than the pending USD profit, D-101)
        return;
      }
      const res = await gateway.commitPreview(pv, { pinMessage: t("pf.pin_convert", { usd: fmt(String(input["usd"])), iqd: fmt(pv.derived["iqd"] ?? "0") }), successKey: "pf.toast_converted" });
      if (res) {
        setConvOpen(false);
        setConvValues({ amount: "", rate: "", note: "" });
        setConvPreview(null);
        invalidate();
      }
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  const submitExpense = () => {
    const amount = amountOf(expValues.amount);
    if (!amount) return toast(t("pf.err_amount"), "error");
    if (expValues.category === "salary" && !expValues.employeeId) return toast(t("pf.err_employee"), "error");
    const category = expValues.category === "withdrawal_comm" ? "withdrawal_commission" : expValues.category;
    void post(
      "expense",
      { amount, category, accountId: data?.vaults.IQD ?? "", ...(expValues.category === "salary" ? { employeeUserId: expValues.employeeId } : {}), ...(expValues.note.trim() ? { note: expValues.note.trim() } : {}) },
      t("pf.pin_expense", { amount: fmt(amount) }),
      "pf.toast_expense",
      () => {
        setExpOpen(false);
        setExpValues({ category: "salary", amount: "", note: "", employeeId: "" });
      },
    );
  };

  // ---------------------------------------------------------------- cancel (journal, D-091 / D-102)
  const [cancelId, setCancelId] = useState<string | null>(null);
  const cancelCode = (() => {
    if (!cancelId || !data) return null;
    const r = data.profits.rows.find((x) => x.id === cancelId) ?? data.profits.liquidation.find((x) => x.id === cancelId) ?? data.expenses.rows.find((x) => x.id === cancelId);
    return r ? `#${r.code}` : null;
  })();
  const confirmCancel = async (reason: string) => {
    if (!cancelId) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.journalCancel({ operationIds: [cancelId], reason, idempotencyKey: crypto.randomUUID() }, proof.pin ?? ""), { message: t("jr.pin_cancel", { n: 1 }), allowSupervisor: false });
      if (!res) return;
      toast(res.status === "pending" ? t("jr.toast_pending") : t("pf.toast_cancelled"), "success");
      setCancelId(null);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- prints (p03 and the expenses statement)
  const [report, setReport] = useState<"profits" | "expenses" | null>(null);
  useEffect(() => {
    if (!report) return;
    const done = () => setReport(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [report]);
  const printDate = (officeDay: string, hhmm: string): string => `${officeDay} ${hhmm}`;

  const years = today ? Array.from({ length: 10 }, (_, i) => String(Number(today.slice(0, 4)) - i)) : [];
  const months = data ? Array.from(new Set([...(month ? [month] : []), ...data.months])).sort().reverse() : [];
  const empty: FinancialsAmount = { iqd: "0", usd: null };
  const v = view;

  return (
    <>
      <Financials
        header={v?.header ?? { revenue: empty, profit: empty, expense: empty, net: empty, netNegative: false }}
        period={period}
        onPeriodChange={setPeriod}
        day={day}
        onDayChange={setDay}
        quickDays={today ? [today, addDays(today, -1), addDays(today, -2)] : ["", "", ""]}
        month={month}
        months={months}
        onMonthChange={setMonth}
        year={year}
        years={years}
        onYearChange={setYear}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        onPreset={(days) => {
          setFrom(addDays(today, -days));
          setTo(today);
        }}
        tab={tab}
        onTabChange={setTab}
        showConvert={!!data && !data.usdHidden && hasPermission("settle_profit")}
        convertBadge={`$${fmt(data?.pending["USD"] ?? "0")}`}
        onConvert={() => {
          setConvValues({ amount: "", rate: data?.usdBuyQuote ?? "", note: "" });
          setConvPreview(null);
          setConvOpen(true);
        }}
        canSettle={hasPermission("settle_profit")}
        canCancel={hasPermission("delete_movements")}
        onCapitalize={() => setCapOpen(true)}
        onWithdraw={() => setWdOpen(true)}
        profitViewMode={profitMode}
        onProfitViewMode={onMode(setProfitMode)}
        onProfitShare={() => setReport("profits")}
        onProfitPdf={() => setReport("profits")}
        onProfitPrint={() => setReport("profits")}
        profitSearch={profitSearch}
        onProfitSearch={setProfitSearch}
        profitTotal={v?.profitTotal ?? empty}
        showCurrencySales={!!data && data.profits.currencySales !== null}
        currencySales={v?.currencySales ?? []}
        currencySalesTotal={v?.currencySalesTotal ?? "0 د.ع"}
        byBox={v?.byBox ?? []}
        byCategory={v?.byCategory ?? []}
        byOperator={v?.byOperator ?? []}
        profitDaily={v?.profitDaily ?? []}
        profitLogDate={profitDate}
        onProfitLogDate={setProfitDate}
        profitRows={v?.profitRows ?? []}
        onCancelOperation={setCancelId}
        liquidation={v?.liquidation ?? []}
        onAddExpense={() => setExpOpen(true)}
        expenseViewMode={expenseMode}
        onExpenseViewMode={onMode(setExpenseMode)}
        onExpenseShare={() => setReport("expenses")}
        onExpensePdf={() => setReport("expenses")}
        onExpensePrint={() => setReport("expenses")}
        expenseSearch={expenseSearch}
        onExpenseSearch={setExpenseSearch}
        expenseTotal={fmt(data?.expenses.total ?? "0")}
        expenseCount={data?.expenses.count ?? 0}
        expenseCards={v?.expenseCards ?? { salary: { total: "0", count: 0, pct: "0" }, tip: { total: "0", count: 0, pct: "0" }, outlet: { total: "0", count: 0, pct: "0" }, withdrawal_comm: { total: "0", count: 0, pct: "0" }, other: { total: "0", count: 0, pct: "0" } }}
        expenseOther={v?.expenseOther ?? { total: "0", count: 0, pct: "0" }}
        salaries={v?.salaries ?? []}
        expenseOperators={v?.expenseOperators ?? []}
        expenseDaily={v?.expenseDaily ?? []}
        expenseLogDate={expenseDate}
        onExpenseLogDate={setExpenseDate}
        expenseRows={v?.expenseRows ?? []}
      />
      <CapitalizeProfitModal open={capOpen} values={capValues} onChange={setCapValues} pending={pendingLabel} busy={busy} onSubmit={() => submitSettlement("capitalize")} onClose={() => setCapOpen(false)} />
      <WithdrawProfitModal open={wdOpen} values={wdValues} onChange={setWdValues} pending={pendingLabel} busy={busy} onSubmit={() => submitSettlement("withdraw")} onClose={() => setWdOpen(false)} />
      <ConvertProfitModal
        open={convOpen}
        values={convValues}
        onChange={setConvValues}
        availableUsd={fmt(data?.pending["USD"] ?? "0")}
        equivalent={convPreview && convPreview.key === convKey && convPreview.pv.derived["iqd"] ? fmt(convPreview.pv.derived["iqd"]) : null}
        busy={busy}
        onSubmit={() => void submitConversion()}
        onClose={() => setConvOpen(false)}
      />
      <AddExpenseModal open={expOpen} values={expValues} onChange={setExpValues} employees={data?.employees ?? []} busy={busy} onSubmit={submitExpense} onClose={() => setExpOpen(false)} />
      <JournalCancelDialog open={cancelId !== null} count={1} codeLabel={cancelCode} summary={[]} busy={busy} onConfirm={(r) => void confirmCancel(r)} onClose={() => setCancelId(null)} />
      {gateway.pin.dialog}
      <div className="relative z-[130]">{pin.dialog}</div>
      {report &&
        data &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            {report === "profits" ? (
              <StatementA4
                locale={locale}
                title={t("pf.print_liq_title")}
                dateLabel={new Date().toLocaleDateString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz })}
                docNumber={String(data.profits.liquidation.length)}
                office={printOffice}
                {...(data.period.from ? { periodFrom: data.period.from } : {})}
                {...(data.period.to ? { periodTo: data.period.to } : {})}
                columns={[
                  { header: t("pf.col_date"), key: "date", numeric: true },
                  { header: t("pf.col_type"), key: "type" },
                  { header: t("pf.col_amount"), key: "amount", numeric: true },
                  { header: t("pf.col_note"), key: "note" },
                ]}
                rows={data.profits.liquidation.map((l) => ({
                  date: printDate(l.officeDate, l.time),
                  type: l.type === "profit_withdraw" ? t("pf.liq_withdraw") : t("pf.liq_capitalize"),
                  amount: l.currency === "USD" ? `$${fmt(l.amount)}` : `${fmt(l.amount)} د.ع`,
                  note: l.note || "-",
                }))}
              />
            ) : (
              <StatementA4
                locale={locale}
                title={t("pf.print_exp_title")}
                dateLabel={new Date().toLocaleDateString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz })}
                docNumber={String(data.expenses.count)}
                office={printOffice}
                {...(data.period.from ? { periodFrom: data.period.from } : {})}
                {...(data.period.to ? { periodTo: data.period.to } : {})}
                columns={[
                  { header: t("pf.col_date"), key: "date", numeric: true },
                  { header: t("pf.col_category"), key: "category" },
                  { header: t("pf.col_amount"), key: "amount", numeric: true },
                  { header: t("pf.col_operator"), key: "operator" },
                  { header: t("pf.col_note"), key: "note" },
                ]}
                rows={data.expenses.rows.map((r) => ({
                  date: printDate(r.officeDate, r.time),
                  category: t(r.cardKey),
                  amount: `${fmt(r.amount)} د.ع`,
                  operator: r.category === "salary" && r.employee ? `${r.operator ?? "-"} → ${r.employee}` : (r.operator ?? "-"),
                  note: r.note || "-",
                }))}
                summary={[{ label: t("pf.total_expenses"), value: `${fmt(data.expenses.total)} د.ع` }]}
              />
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
